import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react"
import toast from "react-hot-toast"
import { useAuth } from "../hooks/useAuth"
import { useEquipments, type Equipment } from "../hooks/useEquipments"
import {
  useEquipmentIssues,
  useEquipmentComments,
  type EquipmentIssue,
  type IssueInput,
  type IssueStatus,
  type ApplyStatus,
  type MentionedUser,
  type IssueAttachment,
  type EquipmentComment,
} from "../hooks/useEquipmentIssues"
import { lumi } from "../lib/lumi"
import { EquipmentIssueFormModal } from "./EquipmentIssueFormModal"
import { EquipmentUnitManager } from "./EquipmentUnitManager"
import { EquipmentResolutionModal } from "./EquipmentResolutionModal"
import { EquipmentRecentActivity } from "./EquipmentRecentActivity"
import { EquipmentIssuePdfExport } from "./EquipmentIssuePdfExport"
import { EquipmentIssueSinglePdfButton } from "./EquipmentIssueSinglePdfButton"
import { ImageLightbox, type LightboxImage } from "./ImageLightbox"
import {
  useEquipmentNotify,
  useSelectableUsers,
  buildIssueEmail,
} from "../hooks/useEquipmentNotify"
import { buildIssueDeepLinkUrl } from "../lib/issueLink"

interface Props {
  equipment: Equipment
  onBack: () => void
  // 딥링크로 진입 시 자동 스크롤/하이라이트할 이슈 ID
  focusIssueId?: string | null
}

const STATUS_META: Record<IssueStatus, { label: string; className: string }> = {
  issued: { label: "이슈 등록", className: "bg-red-50 text-red-600" },
  checking: { label: "확인중", className: "bg-amber-50 text-amber-700" },
  resolved: { label: "조치완료", className: "bg-emerald-50 text-emerald-700" },
  unavailable: { label: "조치불가", className: "bg-slate-100 text-slate-600" },
}

// 조치 종료(상태 변경 잠금 + 적용상태 노출 대상) 상태
function isClosedStatus(status: IssueStatus) {
  return status === "resolved" || status === "unavailable"
}

const APPLY_META: Record<ApplyStatus, { label: string; className: string }> = {
  not_applied: { label: "미적용", className: "bg-red-100 text-red-600 ring-1 ring-red-300" },
  applied: { label: "적용", className: "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-300" },
}

// 첨부파일이 이미지인지 판단 (type 또는 확장자 기준)
function isImageAttachment(a: { url: string; name?: string; type?: string }) {
  if (a.type === "image") return true
  if (a.type && a.type.startsWith("image/")) return true
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.name || a.url || "")
}

function formatDateTime(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate()
  ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

// 읽음 확인 시각 표시용 (YYYY-MM-DD HH:MM)
function formatReadAt(value?: string) {
  if (!value) return "-"
  const d = new Date(value)
  if (isNaN(d.getTime())) return "-"
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

// 코멘트 본문 렌더링 (@멘션 강조)
function renderContentWithMentions(text: string, mentions?: MentionedUser[]) {
  if (!mentions || mentions.length === 0) return text
  const names = mentions.map((m) => m.userName)
  // @이름 패턴을 강조
  const parts = text.split(/(@[^\s@]+)/g)
  return parts.map((p, i) => {
    if (p.startsWith("@") && names.includes(p.slice(1))) {
      return (
        <span key={i} className="text-orange-600 font-medium">
          {p}
        </span>
      )
    }
    return <span key={i}>{p}</span>
  })
}

// 이슈별 코멘트 스레드 (@멘션 + 이메일 알림)
function CommentThread({
  issue,
  equipmentId,
  equipmentName,
  onImageClick,
  onCommentsChange,
}: {
  issue: EquipmentIssue
  equipmentId: string
  equipmentName: string
  onImageClick: (images: LightboxImage[], index: number) => void
  onCommentsChange?: () => void
}) {
  const auth = useAuth()
  const { comments, fetchComments, createComment, deleteComment, markCommentRead } = useEquipmentComments()
  const { users } = useSelectableUsers()
  const { sendNotifications } = useEquipmentNotify()
  const [content, setContent] = useState("")
  const [sending, setSending] = useState(false)
  const [showMention, setShowMention] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [attachments, setAttachments] = useState<IssueAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  // 읽음 상세 팝업으로 보여줄 코멘트의 readBy 목록
  const [readModal, setReadModal] = useState<ReadByEntry[] | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // 코멘트 등록/삭제/읽음 처리로 목록이 갱신돼도 스크롤 위치를 유지
  const savedScrollY = useRef<number | null>(null)
  const rememberScroll = useCallback(() => {
    savedScrollY.current = window.scrollY
  }, [])

  useEffect(() => {
    fetchComments(issue._id)
  }, [fetchComments, issue._id])

  // 코멘트 목록 갱신 후, 액션 직전에 기록한 스크롤 위치로 복원
  useLayoutEffect(() => {
    if (savedScrollY.current === null) return
    const y = savedScrollY.current
    savedScrollY.current = null
    window.scrollTo(0, y)
  }, [comments])

  // comments가 변경될 때마다(로드/등록/읽음/삭제 후) 상위의 안읽은 코멘트 수를 재집계
  // onCommentsChange는 ref로 참조해 불필요한 재실행/무한 루프를 방지
  const onCommentsChangeRef = useRef(onCommentsChange)
  useEffect(() => {
    onCommentsChangeRef.current = onCommentsChange
  }, [onCommentsChange])
  useEffect(() => {
    onCommentsChangeRef.current?.()
  }, [comments])

  // 입력 변화 감지: 마지막 @토큰을 추출해 추천 표시
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setContent(val)
    const match = val.match(/@([^\s@]*)$/)
    if (match) {
      setMentionQuery(match[1].toLowerCase())
      setShowMention(true)
    } else {
      setShowMention(false)
    }
  }

  const mentionCandidates = useMemo(() => {
    return users
      .filter(
        (u) =>
          !mentionQuery ||
          u.userName.toLowerCase().includes(mentionQuery) ||
          u.email.toLowerCase().includes(mentionQuery)
      )
      .slice(0, 6)
  }, [users, mentionQuery])

  const applyMention = (userName: string) => {
    // 마지막 @토큰을 선택한 이름으로 치환
    const replaced = content.replace(/@([^\s@]*)$/, `@${userName} `)
    setContent(replaced)
    setShowMention(false)
    inputRef.current?.focus()
  }

  // 본문에서 실제 등록 사용자와 일치하는 @멘션만 추출
  const extractMentions = (text: string): MentionedUser[] => {
    const found: MentionedUser[] = []
    const seen = new Set<string>()
    for (const u of users) {
      // @이름 (뒤에 공백/끝) 패턴이 본문에 존재하면 멘션으로 인정
      const re = new RegExp(`@${u.userName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\s|$)`)
      if (re.test(text) && !seen.has(u.userId)) {
        seen.add(u.userId)
        found.push({ userId: u.userId, userName: u.userName, email: u.email })
      }
    }
    return found
  }

  // 코멘트 첨부파일 업로드 (사진 + 문서)
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    if (attachments.length + files.length > 5) {
      toast.error("첨부파일은 최대 5개까지 가능합니다.")
      return
    }
    setUploading(true)
    try {
      const uploaded = await lumi.tools.file.upload(files)
      const arr = Array.isArray(uploaded) ? uploaded : [uploaded]
      const next: IssueAttachment[] = arr.map((f: any) => ({
        url: f.fileUrl || f.url,
        name: f.fileName || f.name || "첨부파일",
        type: (f.fileName || f.name || "").match(/\.(png|jpe?g|gif|webp|bmp|svg)$/i) ? "image" : "file",
      }))
      setAttachments((prev) => [...prev, ...next])
    } catch (error) {
      console.error("코멘트 첨부 업로드 실패:", error)
      toast.error("파일 업로드 중 오류가 발생했습니다.")
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  const removeAttachment = (url: string) => {
    setAttachments((prev) => prev.filter((a) => a.url !== url))
  }

  const handleSend = async () => {
    if (!content.trim() && attachments.length === 0) return
    rememberScroll()
    setSending(true)
    const text = content.trim()
    const mentions = extractMentions(text)
    try {
      await createComment(
        issue._id,
        equipmentId,
        text,
        {
          creator: auth.user?.userId || "",
          creatorName: auth.userName || "",
          creatorEmail: auth.user?.email || "",
        },
        mentions,
        attachments
      )
      setContent("")
      setAttachments([])
      setShowMention(false)

      // 코멘트 이메일 알림 대상 (멘션 여부와 무관하게 항상 발송):
      //  - 이슈 작성자
      //  - 이슈 참조자 전원
      //  - @멘션된 사용자
      //  (코멘트 작성 본인 제외 + 이메일 기준 중복 제거 → 동일 대상은 1회만 발송)
      const recipientsMap = new Map<string, { email: string; userName: string }>()
      const addRecipient = (email?: string, userName?: string, userId?: string) => {
        const mail = (email || "").trim()
        if (!mail) return
        if (userId && userId === auth.user?.userId) return
        // 멘션이 아닌 참조자/작성자도 항상 포함하되, 코멘트 작성 본인 이메일은 제외
        if (mail.toLowerCase() === (auth.user?.email || "").trim().toLowerCase()) return
        recipientsMap.set(mail.toLowerCase(), { email: mail, userName: userName || mail })
      }
      // 1) 이슈 작성자
      addRecipient(issue.creatorEmail, issue.creatorName, issue.creator)
      // 2) 이슈 참조자 전원
      for (const u of issue.referencedUsers || []) {
        addRecipient(u.email, u.userName, u.userId)
      }
      // 3) @멘션된 사용자 (이미 위에 포함된 대상은 중복 제거됨)
      for (const m of mentions) {
        addRecipient(m.email, m.userName, m.userId)
      }
      const recipients = Array.from(recipientsMap.values())
      if (recipients.length > 0) {
        const html = buildIssueEmail({
          title: `[새 코멘트] ${auth.userName}님이 코멘트를 남겼습니다`,
          equipmentName,
          issueContent: issue.title || issue.issueContent,
          actorName: auth.userName || "-",
          extraLine: text,
          extraLabel: "코멘트",
          classification: (issue.tags || []).length > 0 ? issue.tags!.join(", ") : undefined,
          linkUrl: buildIssueDeepLinkUrl({ equipmentId, issueId: issue._id }),
        })
        await sendNotifications(
          recipients,
          `[페이브텍] 이슈에 새 코멘트가 등록되었습니다`,
          html,
          auth.user?.email
        )
      }
    } catch (error) {
      console.error("코멘트 등록 실패:", error)
      toast.error("코멘트 등록 중 오류가 발생했습니다.")
    } finally {
      setSending(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("코멘트를 삭제하시겠습니까?")) return
    try {
      rememberScroll()
      await deleteComment(issue._id, id)
    } catch (error) {
      console.error("코멘트 삭제 실패:", error)
      toast.error("삭제 중 오류가 발생했습니다.")
    }
  }

  // 코멘트 읽음 처리 (한 번 읽으면 취소 불가)
  const handleMarkRead = async (comment: EquipmentComment) => {
    const reader = { userId: auth.user?.userId || "", userName: auth.userName || "" }
    if (!reader.userId) return
    if ((comment.readBy || []).some((r) => r.userId === reader.userId)) return
    try {
      rememberScroll()
      await markCommentRead(issue._id, comment, reader)
    } catch (error) {
      console.error("읽음 처리 실패:", error)
      toast.error("읽음 처리 중 오류가 발생했습니다.")
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-500 mb-2">코멘트 {comments.length}</p>
      <div className="space-y-2">
        {comments.map((c) => {
          const canDelete = auth.isAdmin || c.creator === auth.user?.userId
          const readList = c.readBy || []
          const isOwnComment = c.creator === auth.user?.userId
          const hasRead = readList.some((r) => r.userId === auth.user?.userId)
          return (
            <div key={c._id} className="flex items-start gap-2 text-sm">
              <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-700">{c.creatorName || "-"}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* 읽은 사용자 수 → 클릭 시 확인 시각 팝업 */}
                    {readList.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setReadModal(readList)}
                        title="읽음 확인 상세 보기"
                        className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600 hover:text-emerald-700 hover:underline"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {readList.length}
                      </button>
                    )}
                    {/* 읽음 처리 체크 버튼 (본인 작성 코멘트 제외) */}
                    {!isOwnComment && (
                      hasRead ? (
                        <span
                          title="이미 읽음"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white cursor-default"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleMarkRead(c)}
                          title="읽음 처리 (취소 불가)"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 text-gray-300 hover:border-emerald-500 hover:text-emerald-500 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      )
                    )}
                    <span className="text-xs text-gray-400">{formatDateTime(c.createdAt)}</span>
                  </div>
                </div>
                <p className="text-gray-600 whitespace-pre-line mt-0.5">
                  {renderContentWithMentions(c.content, c.mentionedUsers)}
                </p>
                {(c.attachments || []).length > 0 && (() => {
                  const commentImages = (c.attachments || [])
                    .filter((a) => isImageAttachment(a))
                    .map((a) => ({ url: a.url, name: a.name }))
                  return (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.attachments!.map((a) =>
                      isImageAttachment(a) ? (
                        <button
                          key={a.url}
                          type="button"
                          onClick={() =>
                            onImageClick(
                              commentImages,
                              commentImages.findIndex((im) => im.url === a.url)
                            )
                          }
                          className="rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-orange-400 bg-gray-50"
                        >
                          <img
                            src={a.url}
                            alt={a.name}
                            loading="lazy"
                            className="block max-w-[200px] max-h-[200px] w-auto h-auto object-contain"
                          />
                        </button>
                      ) : (
                        <a
                          key={a.url}
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 max-w-[180px] px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:ring-2 hover:ring-orange-400"
                        >
                          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-[11px] text-gray-600 truncate">{a.name || "첨부파일"}</span>
                        </a>
                      )
                    )}
                  </div>
                  )
                })()}
              </div>
              {canDelete && (
                <button
                  onClick={() => handleDelete(c._id)}
                  className="text-xs text-red-400 hover:text-red-600 shrink-0 mt-2"
                >
                  삭제
                </button>
              )}
            </div>
          )
        })}
      </div>
      {/* 선택된 첨부 미리보기 */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {attachments.map((a) => (
            <div
              key={a.url}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-orange-200 bg-orange-50 text-xs text-gray-700"
            >
              <span className="truncate max-w-[140px]">{a.name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.url)}
                className="text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="relative mt-2">
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.zip"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="파일 첨부 (사진/문서)"
            className="shrink-0 p-1.5 text-gray-400 hover:text-orange-600 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-orange-200 border-t-orange-600 rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            )}
          </button>
          <textarea
            ref={inputRef}
            rows={2}
            value={content}
            onChange={handleChange}
            onKeyDown={(e) => {
              // Enter는 줄바꿈, Ctrl/Cmd+Enter로 등록
              if (
                e.key === "Enter" &&
                (e.metaKey || e.ctrlKey) &&
                !e.nativeEvent.isComposing
              ) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="코멘트 입력 (@로 멘션, Ctrl+Enter로 등록)"
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-y min-h-[3.5rem]"
          />
          <button
            onClick={handleSend}
            disabled={sending || (!content.trim() && attachments.length === 0)}
            className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            등록
          </button>
        </div>
        {showMention && mentionCandidates.length > 0 && (
          <ul className="absolute z-20 left-0 right-16 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {mentionCandidates.map((u) => (
              <li key={u.userId}>
                <button
                  type="button"
                  onClick={() => applyMention(u.userName)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-orange-50"
                >
                  <span className="font-medium text-gray-700">@{u.userName}</span>
                  {u.teamName ? <span className="text-gray-400"> · {u.teamName}</span> : null}
                  <span className="block text-xs text-gray-400">{u.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 읽음 확인 상세 팝업 (사용자별 확인 시각) */}
      {readModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setReadModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">
                읽음 확인 ({readModal.length})
              </h3>
              <button
                type="button"
                onClick={() => setReadModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ul className="divide-y divide-gray-100 overflow-y-auto">
              {readModal.map((r) => (
                <li key={r.userId} className="flex items-center justify-between gap-3 px-5 py-2.5">
                  <span className="flex items-center gap-2 text-sm text-gray-700">
                    <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {r.userName}
                  </span>
                  <span className="text-xs text-gray-500 tabular-nums">{formatReadAt(r.readAt)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export function EquipmentIssuePanel({ equipment, onBack, focusIssueId }: Props) {
  const auth = useAuth()
  const {
    issues,
    loading,
    fetchIssues,
    createIssue,
    updateIssue,
    updateIssueStatus,
    updateApplyStatus,
    updateResolution,
    deleteIssue,
  } = useEquipmentIssues()
  const { updateUnits } = useEquipments()
  const { sendNotifications } = useEquipmentNotify()

  // 호기 목록 로컬 상태 (추가/삭제 즉시 반영)
  const [units, setUnits] = useState(equipment.units || [])
  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<EquipmentIssue | null>(null)
  // 조치 결과 등록 모달 대상 이슈
  const [resolutionTarget, setResolutionTarget] = useState<EquipmentIssue | null>(null)
  // 조치완료로 전환 중 결과 입력을 유도한 경우, 결과 저장 후 resolved로 바꿀 대상
  const [pendingResolve, setPendingResolve] = useState<EquipmentIssue | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // 이미지 라이트박스 (같은 화면 팝업)
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null)
  const openLightbox = useCallback((images: LightboxImage[], index: number) => {
    if (images.length === 0 || index < 0) return
    setLightbox({ images, index })
  }, [])
  // 조치불가(unavailable) 이슈 펼침 상태 (기본 접힘 → 제목만 표시)
  const [expandedUnavailable, setExpandedUnavailable] = useState<Set<string>>(new Set())

  // 딥링크 포커스용: 이슈 카드 DOM 참조 맵 + 하이라이트 대상 ID
  const issueRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [highlightId, setHighlightId] = useState<string | null>(null)

  // 스크롤 위치 보존: 코멘트 등록/삭제, 이슈 상태 변경 등으로 목록이 재조회되어도
  // 사용자가 보고 있던 위치를 유지하도록, 액션 직전 scrollY를 기록하고 목록 갱신 후 복원
  const savedScrollY = useRef<number | null>(null)
  const rememberScroll = useCallback(() => {
    savedScrollY.current = window.scrollY
  }, [])

  const toggleUnavailableExpand = (id: string) => {
    setExpandedUnavailable((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  // 호기 필터: "all" | "common" | unitId
  const [unitFilter, setUnitFilter] = useState<string>("all")
  // 작업 상태 필터: "all" | IssueStatus
  const [statusFilter, setStatusFilter] = useState<"all" | IssueStatus>("all")
  // 태그 검색어 + 선택된 태그(배지 클릭)
  const [tagSearch, setTagSearch] = useState("")
  const [activeTag, setActiveTag] = useState<string | null>(null)

  // 태그 배지 클릭 → 토글 필터
  const toggleTagFilter = (tag: string) => {
    setActiveTag((prev) => (prev === tag ? null : tag))
  }

  // 팀장급 이상(팀장/임원/관리자) 여부 - 호기 관리 및 조치결과 수정 권한 기준
  const isTeamLeadOrAbove = auth.isAdmin || auth.isManager || auth.isCLevel

  // 권한: 팀장 이상만 호기 등록/삭제 가능
  const canManageUnits = isTeamLeadOrAbove

  // 조치결과 등록/수정 권한: 로그인한 모든 사용자 가능
  const canEditResolution = useCallback(
    (_issue: EquipmentIssue) => Boolean(auth.user?.userId),
    [auth.user?.userId]
  )

  const handleUnitsChange = async (next: typeof units) => {
    setUnits(next)
    try {
      await updateUnits(equipment._id, next)
    } catch (error) {
      console.error("호기 저장 실패:", error)
      toast.error("호기 정보 저장 중 오류가 발생했습니다.")
      setUnits(equipment.units || [])
    }
  }

  const loadIssues = useCallback(() => {
    fetchIssues(equipment._id)
  }, [fetchIssues, equipment._id])

  useEffect(() => {
    loadIssues()
  }, [loadIssues])

  // 딥링크 포커스: focusIssueId가 있고 해당 이슈가 로드되면
  // 종결(접힘) 이슈는 자동 펼치고, 해당 카드로 스크롤 + 잠시 하이라이트
  useEffect(() => {
    if (!focusIssueId || loading) return
    const target = issues.find((i) => i._id === focusIssueId)
    if (!target) return

    // 종결 이슈는 기본 접힘 상태이므로 자동으로 펼침
    if (isClosedStatus(target.status)) {
      setExpandedUnavailable((prev) => {
        if (prev.has(focusIssueId)) return prev
        const next = new Set(prev)
        next.add(focusIssueId)
        return next
      })
    }

    // DOM 렌더링 후 스크롤 + 하이라이트 (펼침 반영 시간 고려)
    const timer = window.setTimeout(() => {
      const el = issueRefs.current[focusIssueId]
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        setHighlightId(focusIssueId)
        // 하이라이트는 잠시 후 해제
        window.setTimeout(() => setHighlightId(null), 2600)
      }
    }, 350)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIssueId, loading, issues])

  // 이슈별 '내가 읽지 않은 코멘트 수' 집계 (버튼 클릭 전에 미리 표시)
  // key: issueId, value: 안읽은 코멘트 수
  const [unreadByIssue, setUnreadByIssue] = useState<Record<string, number>>({})
  // 최근 활동 요약 카드용: 장비 전체 코멘트 (집계와 동일 조회 결과를 재사용)
  const [allComments, setAllComments] = useState<EquipmentComment[]>([])

  const refreshUnread = useCallback(async () => {
    const myId = auth.user?.userId
    if (!equipment._id) {
      setUnreadByIssue({})
      setAllComments([])
      return
    }
    try {
      // 최근 활동 카드/안읽은 집계 모두 이 결과를 사용하므로,
      // 기본 페이지 크기로 잘리지 않도록 최신순 정렬 + 충분한 개수를 명시
      const { list } = await lumi.entities.equipment_comments.list({
        filter: { equipmentId: equipment._id },
        sort: { createdAt: -1 },
        limit: 500,
      })
      const rows = (list || []) as EquipmentComment[]
      setAllComments(rows)
      if (!myId) {
        setUnreadByIssue({})
        return
      }
      const counts: Record<string, number> = {}
      for (const c of rows) {
        // 본인이 작성한 코멘트는 제외, 이미 읽음(readBy에 본인 포함)은 제외
        if (c.creator === myId) continue
        const hasRead = (c.readBy || []).some((r) => r.userId === myId)
        if (hasRead) continue
        counts[c.issueId] = (counts[c.issueId] || 0) + 1
      }
      setUnreadByIssue(counts)
      console.log("unread comment counts refreshed:", Object.keys(counts).length)
    } catch (error) {
      console.error("안읽은 코멘트 집계 실패:", error)
    }
  }, [equipment._id, auth.user?.userId])

  // 이슈 목록 변화 시 안읽은 코멘트 수 재집계
  useEffect(() => {
    refreshUnread()
  }, [refreshUnread, issues])

  // 이슈 목록 재조회(fetchIssues) 후, 사용자가 보던 스크롤 위치를 복원
  // 페인트 직전(useLayoutEffect)에 복원해 최상단으로 튀는 까박임을 방지
  useLayoutEffect(() => {
    if (savedScrollY.current === null) return
    const y = savedScrollY.current
    savedScrollY.current = null
    window.scrollTo(0, y)
  }, [issues])

  // 전체 이슈에서 사용된 태그 목록 (중복 제거, 정렬)
  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const i of issues) {
      for (const t of i.tags || []) set.add(t)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ko"))
  }, [issues])

  // 호기 + 태그 필터만 적용한 중간 목록 (상태별 개수 집계 기준)
  const scopedIssues = useMemo(() => {
    let result = issues
    // 호기 필터
    if (unitFilter === "common") result = result.filter((i) => !i.unitId)
    else if (unitFilter !== "all") result = result.filter((i) => i.unitId === unitFilter)
    // 선택된 태그(배지 클릭) 필터 - 정확히 해당 태그 포함
    if (activeTag) {
      result = result.filter((i) => (i.tags || []).includes(activeTag))
    }
    // 태그 검색어 필터 - 부분 일치
    const q = tagSearch.trim().toLowerCase()
    if (q) {
      result = result.filter((i) =>
        (i.tags || []).some((t) => t.toLowerCase().includes(q))
      )
    }
    return result
  }, [issues, unitFilter, activeTag, tagSearch])

  // 상태별 개수 (호기/태그 필터 적용 범위 기준) + 전체 개수
  const statusCounts = useMemo(() => {
    const counts: Record<IssueStatus, number> = {
      issued: 0,
      checking: 0,
      resolved: 0,
      unavailable: 0,
    }
    for (const i of scopedIssues) {
      if (counts[i.status] !== undefined) counts[i.status] += 1
    }
    return counts
  }, [scopedIssues])

  // 상태 필터까지 적용된 최종 이슈 목록
  const filteredIssues = useMemo(() => {
    if (statusFilter === "all") return scopedIssues
    return scopedIssues.filter((i) => i.status === statusFilter)
  }, [scopedIssues, statusFilter])

  // 호기 ID → 라벨 매핑 (카드 표시용)
  const unitLabelMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const u of units) {
      const sn = u.serialNumber ? `${u.serialNumber} · ` : ""
      m.set(u.unitId, `${sn}${u.unitName}`)
    }
    return m
  }, [units])

  // 최근 활동 카드에서 항목 클릭 → 해당 이슈 카드로 이동 + 하이라이트
  // 필턴로 인해 목록에서 안 보이는 경우가 없도록 필터를 초기화하고, 종결 이슈는 펼침
  const handleSelectActivityIssue = useCallback(
    (issueId: string) => {
      const target = issues.find((i) => i._id === issueId)
      if (!target) return
      setUnitFilter("all")
      setStatusFilter("all")
      setActiveTag(null)
      setTagSearch("")
      if (isClosedStatus(target.status)) {
        setExpandedUnavailable((prev) => {
          if (prev.has(issueId)) return prev
          const next = new Set(prev)
          next.add(issueId)
          return next
        })
      }
      window.setTimeout(() => {
        const el = issueRefs.current[issueId]
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
          setHighlightId(issueId)
          window.setTimeout(() => setHighlightId(null), 2600)
        }
      }, 120)
    },
    [issues]
  )

  const openCreate = () => {
    setEditTarget(null)
    setShowModal(true)
  }

  const openEdit = (issue: EquipmentIssue) => {
    setEditTarget(issue)
    setShowModal(true)
  }

  const handleSubmit = async (data: IssueInput) => {
    if (editTarget) {
      // 기존 참조자 ID 집합 (수정 전 시점)
      const prevRefIds = new Set(
        (editTarget.referencedUsers || []).map((u) => u.userId)
      )
      await updateIssue(equipment._id, editTarget._id, data)

      // 이메일 알림: 수정으로 새로 추가된 참조자에게만 발송 (기존 참조자 제외)
      const newRefs = (data.referencedUsers || []).filter(
        (u) => !prevRefIds.has(u.userId) && u.userId !== auth.user?.userId
      )
      if (newRefs.length > 0) {
        const html = buildIssueEmail({
          title: `[참조자 추가] ${auth.userName}님이 회원님을 참조자로 지정했습니다`,
          equipmentName: equipment.productName,
          issueContent: data.title || data.issueContent,
          actorName: auth.userName || "-",
          linkUrl: buildIssueDeepLinkUrl({ equipmentId: equipment._id, issueId: editTarget._id }),
        })
        await sendNotifications(
          newRefs.map((u) => ({ email: u.email, userName: u.userName })),
          `[페이브텍] 이슈의 참조자로 지정되었습니다`,
          html,
          auth.user?.email
        )
      }
    } else {
      const created = await createIssue(equipment._id, data, {
        creator: auth.user?.userId || "",
        creatorName: auth.userName || "",
        creatorEmail: auth.user?.email || "",
      })
      // 이메일 알림: 이슈 신규 등록 → 참조자 전원
      const refs = data.referencedUsers || []
      const newIssueId = (created as any)?._id as string | undefined
      if (refs.length > 0) {
        const html = buildIssueEmail({
          title: `[이슈 등록] ${auth.userName}님이 새 이슈를 등록했습니다`,
          equipmentName: equipment.productName,
          issueContent: data.title || data.issueContent,
          actorName: auth.userName || "-",
          linkUrl: newIssueId
            ? buildIssueDeepLinkUrl({ equipmentId: equipment._id, issueId: newIssueId })
            : undefined,
        })
        await sendNotifications(
          refs.map((u) => ({ email: u.email, userName: u.userName })),
          `[페이브텍] 새 이슈의 참조자로 지정되었습니다`,
          html,
          auth.user?.email
        )
      }
    }
  }

  const handleStatusChange = async (issue: EquipmentIssue, status: IssueStatus) => {
    // 상태 변경은 로그인한 모든 사용자가 가능 (작성자/참조자 제한 없음)
    if (!auth.user?.userId) {
      toast.error("로그인 후 이용해주세요.")
      return
    }
    if (isClosedStatus(status)) {
      // 조치완료는 현장 적용이 '적용' 상태일 때만 가능
      if (status === "resolved" && issue.applyStatus !== "applied") {
        toast.error("현장 적용 상태가 '적용'일 때만 조치완료를 선택할 수 있습니다.")
        return
      }
      // 조치완료 시 조치 결과가 아직 없으면 결과 입력을 먼저 유도 (결과 저장 후 자동 조치완료)
      if (status === "resolved" && !issue.resolutionContent?.trim()) {
        toast("조치 결과와 사진을 먼저 등록해주세요. 저장하면 자동으로 조치완료로 변경됩니다.", { icon: "📝" })
        setPendingResolve(issue)
        setResolutionTarget(issue)
        return
      }
    }
    try {
      rememberScroll()
      await updateIssueStatus(equipment._id, issue._id, status)
      toast.success("상태가 변경되었습니다.")

      // 이메일 알림: 상태 변경 → 작성자 + 참조자 (변경한 본인 제외)
      const recipientsMap = new Map<string, { email: string; userName: string }>()
      if (issue.creatorEmail && issue.creator !== auth.user?.userId) {
        recipientsMap.set(issue.creatorEmail, { email: issue.creatorEmail, userName: issue.creatorName })
      }
      for (const u of issue.referencedUsers || []) {
        if (u.email && u.userId !== auth.user?.userId) {
          recipientsMap.set(u.email, { email: u.email, userName: u.userName })
        }
      }
      const recipients = Array.from(recipientsMap.values())
      if (recipients.length > 0) {
        const statusLabel = STATUS_META[status].label
        const html = buildIssueEmail({
          title: `[상태 변경] 이슈 상태가 '${statusLabel}'(으)로 변경되었습니다`,
          equipmentName: equipment.productName,
          issueContent: issue.title || issue.issueContent,
          actorName: auth.userName || "-",
          extraLine: `변경된 상태: ${statusLabel}`,
          linkUrl: buildIssueDeepLinkUrl({ equipmentId: equipment._id, issueId: issue._id }),
        })
        await sendNotifications(
          recipients,
          `[페이브텍] 이슈 상태가 변경되었습니다 (${statusLabel})`,
          html,
          auth.user?.email
        )
      }
    } catch (error) {
      console.error("상태 변경 실패:", error)
      toast.error("상태 변경 중 오류가 발생했습니다.")
    }
  }

  // 적용 상태 변경: 누구나 가능
  const handleApplyStatusChange = async (issue: EquipmentIssue, applyStatus: ApplyStatus) => {
    try {
      rememberScroll()
      await updateApplyStatus(equipment._id, issue._id, applyStatus)
      toast.success(`현장 적용 상태가 '${APPLY_META[applyStatus].label}'(으)로 변경되었습니다.`)
    } catch (error) {
      console.error("적용 상태 변경 실패:", error)
      toast.error("적용 상태 변경 중 오류가 발생했습니다.")
    }
  }

  // 조치완료 상태로 전환 + 상태 변경 이메일 알림 (자동 전환에 재사용)
  const resolveIssue = async (issue: EquipmentIssue) => {
    await updateIssueStatus(equipment._id, issue._id, "resolved")

    // 이메일 알림: 상태 변경 → 작성자 + 참조자 (변경한 본인 제외)
    const recipientsMap = new Map<string, { email: string; userName: string }>()
    if (issue.creatorEmail && issue.creator !== auth.user?.userId) {
      recipientsMap.set(issue.creatorEmail, { email: issue.creatorEmail, userName: issue.creatorName })
    }
    for (const u of issue.referencedUsers || []) {
      if (u.email && u.userId !== auth.user?.userId) {
        recipientsMap.set(u.email, { email: u.email, userName: u.userName })
      }
    }
    const recipients = Array.from(recipientsMap.values())
    if (recipients.length > 0) {
      const statusLabel = STATUS_META.resolved.label
      const html = buildIssueEmail({
        title: `[상태 변경] 이슈 상태가 '${statusLabel}'(으)로 변경되었습니다`,
        equipmentName: equipment.productName,
        issueContent: issue.title || issue.issueContent,
        actorName: auth.userName || "-",
        extraLine: `변경된 상태: ${statusLabel}`,
        linkUrl: buildIssueDeepLinkUrl({ equipmentId: equipment._id, issueId: issue._id }),
      })
      await sendNotifications(
        recipients,
        `[페이브텍] 이슈 상태가 변경되었습니다 (${statusLabel})`,
        html,
        auth.user?.email
      )
    }
  }

  // 조치 결과(내용 + 사진) 저장 → 조치완료 유도 흐름이면 자동으로 resolved 전환
  // 모달(EquipmentResolutionModal)이 기본 성공 토스트 및 onClose를 담당하므로,
  // 여기서는 저장 + 자동 상태 전환만 수행하고 오류는 그대로 throw해 모달이 처리하도록 함
  const handleResolutionSubmit = async (data: {
    resolutionContent: string
    resolutionAttachments: IssueAttachment[]
    resolvedByName: string
  }) => {
    if (!resolutionTarget) return
    const target = resolutionTarget
    // 권한 재검증 (팀장급 이상 / 작성자 / 참조자만 저장 가능)
    if (!canEditResolution(target)) {
      toast.error("로그인 후 이용해주세요.")
      return
    }
    // 이 결과 저장이 '조치완료 선택'에서 유도된 것인지 여부
    const shouldResolve = pendingResolve?._id === target._id

    rememberScroll()
    await updateResolution(equipment._id, target._id, data)

    if (shouldResolve) {
      // 결과 저장 직후 자동으로 조치완료 상태로 전환 + 알림
      await resolveIssue(target)
      toast.success("상태가 '조치완료'로 자동 변경되었습니다.")
    }
    // 유도 상태 초기화 (모달 닫기는 onClose/setResolutionTarget이 모달 쪽에서 처리)
    setPendingResolve(null)
  }

  const handleDelete = async (issue: EquipmentIssue) => {
    // 삭제 권한: 작성자 + 관리자
    const canDelete = auth.isAdmin || issue.creator === auth.user?.userId
    if (!canDelete) {
      toast.error("삭제 권한이 없습니다. (작성자/관리자만 가능)")
      return
    }
    if (!window.confirm("이슈를 삭제하시겠습니까? 관련 정보가 모두 사라집니다.")) return
    try {
      await deleteIssue(equipment._id, issue._id)
      toast.success("이슈가 삭제되었습니다.")
    } catch (error) {
      console.error("이슈 삭제 실패:", error)
      toast.error("삭제 중 오류가 발생했습니다.")
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* 상단: 뒤로가기 + 장비 정보 */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        장비 목록으로
      </button>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
        <h1 className="text-xl font-bold text-gray-900">{equipment.productName}</h1>
        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-400">납품업체</dt>
            <dd className="text-gray-700">{equipment.supplier || "-"}</dd>
          </div>
          <div>
            <dt className="text-gray-400">고객사 관리번호</dt>
            <dd className="text-gray-700">{equipment.customerMgmtNumber || "-"}</dd>
          </div>
          <div>
            <dt className="text-gray-400">등록자</dt>
            <dd className="text-gray-700">{equipment.creatorName || "-"}</dd>
          </div>
        </dl>
        {(equipment.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {equipment.tags!.map((t) => (
              <span key={t} className="px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded-full">
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 호기 관리 */}
      <EquipmentUnitManager
        equipment={{ ...equipment, units }}
        canManage={canManageUnits}
        onUpdateUnits={(_, next) => handleUnitsChange(next)}
      />

      {/* 최근 활동 요약 (상태 변경/조치결과/코멘트 + '내 관련 활동만 보기' 토글) */}
      <EquipmentRecentActivity
        issues={issues}
        comments={allComments}
        myUserId={auth.user?.userId}
        myEmail={auth.user?.email}
        myName={auth.userName}
        unreadByIssue={unreadByIssue}
        unitLabelMap={unitLabelMap}
        onSelectIssue={handleSelectActivityIssue}
      />

      {/* 이슈 헤더 */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900">이슈 관리 <span className="text-gray-400 font-normal">({filteredIssues.length})</span></h2>
        <div className="flex items-center gap-2">
          {/* 이슈 이력 PDF 출력 (표지 + 이슈별 구역 분리 출력) */}
          <EquipmentIssuePdfExport
            equipment={{ ...equipment, units }}
            filteredIssues={filteredIssues}
            allIssues={issues}
            comments={allComments}
            unitLabelMap={unitLabelMap}
          />
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            이슈 등록
          </button>
        </div>
      </div>

      {/* 호기 필터 탭 */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <button
          onClick={() => setUnitFilter("all")}
          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
            unitFilter === "all"
              ? "bg-orange-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          전체
        </button>
        <button
          onClick={() => setUnitFilter("common")}
          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
            unitFilter === "common"
              ? "bg-orange-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          공통항목
        </button>
        {units.map((u) => (
          <button
            key={u.unitId}
            onClick={() => setUnitFilter(u.unitId)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              unitFilter === u.unitId
                ? "bg-orange-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {unitLabelMap.get(u.unitId)}
          </button>
        ))}
      </div>

      {/* 작업 상태 필터 탭 */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <span className="text-xs text-gray-400 mr-0.5">상태:</span>
        <button
          onClick={() => setStatusFilter("all")}
          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
            statusFilter === "all"
              ? "bg-gray-800 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          전체 <span className="ml-0.5 opacity-80">{scopedIssues.length}</span>
        </button>
        {(Object.keys(STATUS_META) as IssueStatus[]).map((st) => {
          const active = statusFilter === st
          const count = statusCounts[st]
          return (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                active
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  st === "issued"
                    ? "bg-red-500"
                    : st === "checking"
                    ? "bg-amber-500"
                    : st === "resolved"
                    ? "bg-emerald-500"
                    : "bg-slate-400"
                }`}
              />
              {STATUS_META[st].label}
              <span
                className={`ml-0.5 px-1.5 rounded-full text-[11px] font-semibold ${
                  active ? "bg-white/25 text-white" : "bg-white text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* 태그 검색 + 태그 칩 필터 */}
      <div className="mb-4 space-y-2">
        <div className="relative max-w-xs">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
          <input
            type="text"
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            placeholder="태그로 이슈 검색 (예: 누수)"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          />
          {tagSearch && (
            <button
              type="button"
              onClick={() => setTagSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        {allTags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400">태그:</span>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTagFilter(tag)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  activeTag === tag
                    ? "bg-orange-600 text-white"
                    : "bg-orange-50 text-orange-700 hover:bg-orange-100"
                }`}
              >
                #{tag}
              </button>
            ))}
            {activeTag && (
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 underline"
              >
                필터 해제
              </button>
            )}
          </div>
        )}
      </div>

      {/* 이슈 목록 */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
        </div>
      ) : filteredIssues.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm py-20 text-center">
          <p className="text-gray-500">
            {statusFilter !== "all"
              ? `'${STATUS_META[statusFilter].label}' 상태의 이슈가 없습니다.`
              : activeTag || tagSearch.trim()
              ? "검색 조건에 맞는 이슈가 없습니다."
              : unitFilter === "all"
              ? "등록된 이슈가 없습니다."
              : "해당 분류의 이슈가 없습니다."}
          </p>
          {activeTag || tagSearch.trim() || statusFilter !== "all" ? (
            <button
              type="button"
              onClick={() => {
                setActiveTag(null)
                setTagSearch("")
                setStatusFilter("all")
              }}
              className="text-sm text-orange-600 hover:text-orange-700 underline mt-1"
            >
              필터 초기화
            </button>
          ) : (
            <p className="text-sm text-gray-400 mt-1">상단의 '이슈 등록' 버튼으로 이슈를 추가하세요.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredIssues.map((issue) => {
            const meta = STATUS_META[issue.status]
            const isOwner = issue.creator === auth.user?.userId
            const canEdit = isOwner
            // 상태 변경: 로그인한 모든 사용자 가능
            const canChangeStatus = Boolean(auth.user?.userId)
            const canDelete = auth.isAdmin || isOwner
            const expanded = expandedId === issue._id

            // 조치완료/조치불가(종결) 이슈는 기본 접힘 → 제목만 표시
            const isClosed = isClosedStatus(issue.status)
            const closedExpanded = expandedUnavailable.has(issue._id)
            if (isClosed && !closedExpanded) {
              return (
                <button
                  key={issue._id}
                  ref={(el) => {
                    issueRefs.current[issue._id] = el as unknown as HTMLDivElement | null
                  }}
                  type="button"
                  onClick={() => toggleUnavailableExpand(issue._id)}
                  className={`w-full flex items-center gap-3 bg-white rounded-xl border shadow-sm px-4 py-3 text-left hover:bg-gray-50 transition-all ${
                    highlightId === issue._id
                      ? "border-orange-500 ring-2 ring-orange-400 ring-offset-2"
                      : "border-gray-200"
                  }`}
                >
                  <span className={`px-2 py-0.5 text-xs sm:text-sm sm:px-3 sm:py-1 font-semibold rounded-full shrink-0 ${meta.className}`}>
                    {meta.label}
                  </span>
                  {issue.applyStatus === "applied" && (
                    <span className={`px-2 py-0.5 text-xs sm:text-sm sm:px-3 sm:py-1 font-semibold rounded-full shrink-0 ${APPLY_META.applied.className}`}>
                      현장 적용
                    </span>
                  )}
                  <span className="flex-1 min-w-0 truncate font-medium text-gray-700">
                    {issue.title?.trim() || "제목없음"}
                  </span>
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )
            }

            return (
              <div
                key={issue._id}
                ref={(el) => {
                  issueRefs.current[issue._id] = el
                }}
                className={`bg-white rounded-xl border shadow-sm p-4 transition-all ${
                  highlightId === issue._id
                    ? "border-orange-500 ring-2 ring-orange-400 ring-offset-2"
                    : "border-gray-200"
                }`}
              >
                {isClosed && (
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => toggleUnavailableExpand(issue._id)}
                      className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      접기
                    </button>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs sm:text-sm sm:px-3 sm:py-1 font-semibold rounded-full ${meta.className}`}>
                        {meta.label}
                      </span>
                      {issue.applyStatus === "applied" && (
                        <span
                          className={`px-2 py-0.5 text-xs sm:text-sm sm:px-3 sm:py-1 font-semibold rounded-full ${APPLY_META.applied.className}`}
                        >
                          현장 적용
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 text-xs sm:text-sm sm:px-3 sm:py-1 font-semibold rounded-full ${
                          issue.unitId
                            ? "bg-indigo-50 text-indigo-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {issue.unitId
                          ? unitLabelMap.get(issue.unitId) || issue.unitLabel || "호기"
                          : "공통항목"}
                      </span>
                      <span className="text-xs text-gray-400">{formatDateTime(issue.createdAt)}</span>
                      <span className="text-xs text-gray-500">· {issue.creatorName}</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900 mt-2">{issue.title?.trim() || "제목없음"}</p>

                    {/* 이슈 발생 ↔ 조치 결과: 모바일은 위/아래(1단), 데스크톱은 좌/우(균등 2단) */}
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* 좌: 이슈 발생 */}
                      <div className="min-w-0 rounded-lg border border-red-100 bg-red-50/60 p-3">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="w-2 h-2 rounded-full bg-red-500" />
                          <span className="text-xs font-bold text-red-600">이슈 발생</span>
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-line">{issue.issueContent}</p>
                        {(issue.attachments || []).length > 0 && (() => {
                          const issueImages = (issue.attachments || [])
                            .filter((a) => isImageAttachment(a))
                            .map((a) => ({ url: a.url, name: a.name }))
                          return (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {issue.attachments!.map((a) =>
                              isImageAttachment(a) ? (
                                <button
                                  key={a.url}
                                  type="button"
                                  onClick={() =>
                                    openLightbox(
                                      issueImages,
                                      issueImages.findIndex((im) => im.url === a.url)
                                    )
                                  }
                                  className="rounded-lg overflow-hidden border border-red-200 hover:ring-2 hover:ring-red-400 bg-gray-50"
                                >
                                  <img
                                    src={a.url}
                                    alt={a.name}
                                    loading="lazy"
                                    className="block max-w-full sm:max-w-[240px] max-h-[240px] w-auto h-auto object-contain"
                                  />
                                </button>
                              ) : (
                                <a
                                  key={a.url}
                                  href={a.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 max-w-[160px] px-2.5 py-1.5 rounded-lg border border-red-200 bg-white hover:ring-2 hover:ring-red-400"
                                >
                                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                  <span className="text-[11px] text-gray-600 truncate">{a.name || "첨부파일"}</span>
                                </a>
                              )
                            )}
                          </div>
                          )
                        })()}
                      </div>

                      {/* 우: 조치 결과 */}
                      <div className="min-w-0 rounded-lg border border-emerald-100 bg-emerald-50/60 p-3">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-xs font-bold text-emerald-600">조치 결과</span>
                          </div>
                          {canEditResolution(issue) && (
                            <button
                              type="button"
                              onClick={() => setResolutionTarget(issue)}
                              title="조치결과 등록/수정"
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-white border border-emerald-200 px-2 py-0.5 rounded-md hover:bg-emerald-100"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              {issue.resolutionContent ? "결과 수정" : "결과 등록"}
                            </button>
                          )}
                        </div>
                        {issue.resolutionContent ? (
                          <>
                            <p className="text-gray-700 text-sm whitespace-pre-line">{issue.resolutionContent}</p>
                            {(issue.resolvedByName || issue.resolvedAt) && (
                              <p className="text-[11px] text-emerald-600/80 mt-1">
                                {issue.resolvedByName ? `${issue.resolvedByName} · ` : ""}
                                {formatDateTime(issue.resolvedAt)}
                              </p>
                            )}
                            {(issue.resolutionAttachments || []).length > 0 && (() => {
                              const resImages = (issue.resolutionAttachments || [])
                                .filter((a) => isImageAttachment(a))
                                .map((a) => ({ url: a.url, name: a.name }))
                              return (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {issue.resolutionAttachments!.map((a) =>
                                  isImageAttachment(a) ? (
                                    <button
                                      key={a.url}
                                      type="button"
                                      onClick={() =>
                                        openLightbox(
                                          resImages,
                                          resImages.findIndex((im) => im.url === a.url)
                                        )
                                      }
                                      className="rounded-lg overflow-hidden border border-emerald-200 hover:ring-2 hover:ring-emerald-400 bg-gray-50"
                                    >
                                      <img
                                        src={a.url}
                                        alt={a.name}
                                        loading="lazy"
                                        className="block max-w-full sm:max-w-[240px] max-h-[240px] w-auto h-auto object-contain"
                                      />
                                    </button>
                                  ) : (
                                    <a
                                      key={a.url}
                                      href={a.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 max-w-[160px] px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-white hover:ring-2 hover:ring-emerald-400"
                                    >
                                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      </svg>
                                      <span className="text-[11px] text-gray-600 truncate">{a.name || "첨부파일"}</span>
                                    </a>
                                  )
                                )}
                              </div>
                              )
                            })()}
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 py-2">
                            아직 조치 결과가 등록되지 않았습니다.
                          </p>
                        )}
                      </div>
                    </div>

                    {(issue.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(issue.tags || []).map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleTagFilter(tag)}
                            title={`'${tag}' 태그로 필터`}
                            className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full transition-colors ${
                              activeTag === tag
                                ? "bg-orange-600 text-white"
                                : "bg-orange-50 text-orange-700 hover:bg-orange-100"
                            }`}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    )}
                    {(issue.referencedUsers || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        <span className="text-xs text-gray-400">참조:</span>
                        {issue.referencedUsers!.map((u) => (
                          <span key={u.userId} className="px-1.5 py-0.5 bg-orange-50 text-orange-600 text-[11px] rounded">
                            {u.userName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* 이 이슈 1건만 PDF로 출력 */}
                    <EquipmentIssueSinglePdfButton
                      equipment={{ ...equipment, units }}
                      issue={issue}
                      comments={allComments}
                      unitLabelMap={unitLabelMap}
                    />
                    {canEdit && (
                      <button
                        onClick={() => openEdit(issue)}
                        className="px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded-md hover:bg-orange-100"
                      >
                        수정
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(issue)}
                        className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                </div>

                {/* 상세 정보 */}
                {(issue.expectedCause || issue.possibleAction || issue.fieldComment || issue.actualCost) ? (
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-gray-50 rounded-lg p-3">
                    {issue.expectedCause ? (
                      <div className="sm:col-span-2">
                        <dt className="text-gray-400 text-xs">예상원인 및 History</dt>
                        <dd className="text-gray-700 whitespace-pre-line">{issue.expectedCause}</dd>
                      </div>
                    ) : null}
                    {issue.possibleAction ? (
                      <div className="sm:col-span-2">
                        <dt className="text-gray-400 text-xs">가능한 조치</dt>
                        <dd className="text-gray-700 whitespace-pre-line">{issue.possibleAction}</dd>
                      </div>
                    ) : null}
                    {issue.fieldComment ? (
                      <div>
                        <dt className="text-gray-400 text-xs">현장 코멘트</dt>
                        <dd className="text-gray-700 whitespace-pre-line">{issue.fieldComment}</dd>
                      </div>
                    ) : null}
                    {issue.actualCost ? (
                      <div>
                        <dt className="text-gray-400 text-xs">실제 소요 비용</dt>
                        <dd className="text-gray-700">{issue.actualCost.toLocaleString()}원</dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}

                {/* 상태 변경 + 적용 상태 + 코멘트 토글 */}
                <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">상태 변경:</span>
                      <select
                        value={issue.status}
                        disabled={!canChangeStatus || isClosedStatus(issue.status)}
                        onChange={(e) => handleStatusChange(issue, e.target.value as IssueStatus)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:ring-2 focus:ring-orange-500 outline-none"
                      >
                        <option value="issued">이슈 등록</option>
                        <option value="checking">확인중</option>
                        <option value="resolved" disabled={issue.applyStatus !== "applied"}>
                          조치완료{issue.applyStatus !== "applied" ? " (현장 적용 후)" : ""}
                        </option>
                        <option value="unavailable">조치불가</option>
                      </select>
                      {isClosedStatus(issue.status) && (
                        <span className="text-[11px] text-gray-400">
                          ({STATUS_META[issue.status].label} 이후 상태 변경 불가)
                        </span>
                      )}
                    </div>

                    {/* 현장 적용 상태: 항상 표시, 누구나 변경 가능 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">현장 적용:</span>
                      <select
                        value={issue.applyStatus || "not_applied"}
                        onChange={(e) => handleApplyStatusChange(issue, e.target.value as ApplyStatus)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="not_applied">미적용</option>
                        <option value="applied">적용완료</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* 안읽은 코멘트 수 배지 (코멘트 보기 버튼 왼쪽) */}
                    {(unreadByIssue[issue._id] || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-white bg-red-500 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
                        읽지 않음 {unreadByIssue[issue._id]}개
                      </span>
                    )}
                    <button
                      onClick={() => setExpandedId(expanded ? null : issue._id)}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
                        expanded
                          ? "bg-orange-600 text-white border-orange-600 hover:bg-orange-700"
                          : "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      {expanded ? "코멘트 닫기" : "코멘트 보기"}
                    </button>
                  </div>
                </div>

                {expanded && (
                  <CommentThread
                    issue={issue}
                    equipmentId={equipment._id}
                    equipmentName={equipment.productName}
                    onImageClick={openLightbox}
                    onCommentsChange={refreshUnread}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <EquipmentIssueFormModal
          initial={editTarget}
          units={units}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}

      {/* 조치 결과 등록/수정 모달 */}
      {resolutionTarget && (
        <EquipmentResolutionModal
          issue={resolutionTarget}
          resolverName={auth.userName || ""}
          onClose={() => {
            setResolutionTarget(null)
            setPendingResolve(null)
          }}
          onSubmit={handleResolutionSubmit}
        />
      )}

      {/* 이미지 라이트박스 (새 탭 대신 같은 화면 팝업, 좌우 넘김) */}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(i) =>
            setLightbox((prev) => (prev ? { ...prev, index: i } : prev))
          }
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}
